import React, { useState, useEffect } from 'react';
import { apiService } from '../services';
import {
  Competitor,
  CompetitionType,
  CreateIndividualCompetitorInput,
  CreateTeamCompetitorInput,
  isTeamCompetitor,
  isIndividualCompetitor
} from '../types/competitor';
import { Athlete } from '../types/athlete';
import CompetitorForm from './CompetitorForm';

interface Event {
  id: string;
  name: string;
  competitors: Competitor[];
}

interface CompetitorSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  event: Event | null;
  onCompetitorsUpdated: () => void;
}

const CompetitorSidePanel: React.FC<CompetitorSidePanelProps> = ({ isOpen, onClose, event, onCompetitorsUpdated }) => {
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [loadingAthletes, setLoadingAthletes] = useState(true);

  useEffect(() => {
    if (isOpen) {
      loadAthletes();
    }
  }, [isOpen]);

  const loadAthletes = async () => {
    try {
      setLoadingAthletes(true);
      const data = await apiService.get<Athlete[]>('/athletes');
      setAthletes(data);
    } catch (err: any) {
      console.error('Failed to load athletes:', err);
    } finally {
      setLoadingAthletes(false);
    }
  };

  const getAthleteName = (athleteId: string): string => {
    const athlete = athletes.find(a => a.id === athleteId);
    return athlete ? `${athlete.name} ${athlete.lastName}` : 'Unknown Athlete';
  };

  const handleAddCompetitor = async (
    competitorData: CreateIndividualCompetitorInput | CreateTeamCompetitorInput,
    type: CompetitionType
  ) => {
    if (!event) return;

    setLoading(true);
    try {
      const competitorId = `competitor-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

      const competitor = type === CompetitionType.INDIVIDUAL
        ? {
            type: 'INDIVIDUAL',
            id: competitorId,
            athleteId: (competitorData as CreateIndividualCompetitorInput).athleteId,
            category: competitorData.category,
          }
        : {
            type: 'TEAM',
            id: competitorId,
            athleteIds: (competitorData as CreateTeamCompetitorInput).athleteIds,
            name: (competitorData as CreateTeamCompetitorInput).name,
            category: competitorData.category,
          };

      await apiService.post(`/events/${event.id}/competitors`, {
        competitor,
      });
      setShowForm(false);
      onCompetitorsUpdated();
    } catch (err: any) {
      throw new Error(err.message || 'Failed to add competitor');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCompetitor = async (competitorId: string) => {
    if (!event || !window.confirm('Are you sure you want to delete this competitor?')) return;

    setLoading(true);
    try {
      const updatedCompetitors = event.competitors.filter(c => c.id !== competitorId);
      await apiService.put(`/events/${event.id}`, {
        competitors: updatedCompetitors,
      });
      onCompetitorsUpdated();
    } catch (err: any) {
      alert('Failed to delete competitor: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-40 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Side Panel */}
      <div className="fixed right-0 top-0 h-full w-full md:w-2/3 lg:w-1/2 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Manage Competitors</h2>
            <p className="text-sm text-blue-100">{event?.name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!showForm ? (
            <>
              {/* Add Button */}
              <button
                onClick={() => setShowForm(true)}
                className="w-full mb-6 py-3 px-4 bg-gradient-to-r from-blue-600 to-indigo-700 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-indigo-800 transition-all flex items-center justify-center"
              >
                <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add New Competitor
              </button>

              {/* Competitors List */}
              {loadingAthletes ? (
                <div className="flex justify-center py-12">
                  <svg className="animate-spin h-12 w-12 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              ) : event?.competitors && event.competitors.length > 0 ? (
                <div className="space-y-4">
                  {event.competitors.map((competitor) => (
                    <div
                      key={competitor.id}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          {isTeamCompetitor(competitor) ? (
                            <>
                              <div className="flex items-center mb-2">
                                <svg className="h-5 w-5 text-blue-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                                <h3 className="text-lg font-semibold text-gray-900">{competitor.name}</h3>
                              </div>
                              <div className="ml-7">
                                <p className="text-xs font-medium text-gray-500 mb-1">Team Members:</p>
                                <ul className="text-sm text-gray-700 space-y-1">
                                  {competitor.athleteIds.map((athleteId, idx) => (
                                    <li key={idx} className="flex items-start">
                                      <span className="text-blue-600 mr-2">•</span>
                                      <span>{getAthleteName(athleteId)}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </>
                          ) : isIndividualCompetitor(competitor) ? (
                            <>
                              <div className="flex items-center mb-2">
                                <svg className="h-5 w-5 text-blue-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                <h3 className="text-lg font-semibold text-gray-900">
                                  {getAthleteName(competitor.athleteId)}
                                </h3>
                              </div>
                            </>
                          ) : null}
                        </div>
                        <button
                          onClick={() => handleDeleteCompetitor(competitor.id)}
                          disabled={loading}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 ml-4"
                        >
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>

                      <div className="flex items-center text-sm">
                        <div className="flex items-center text-gray-700">
                          <svg className="h-4 w-4 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          <span className="font-medium">{competitor.category}</span>
                        </div>
                        <span className="mx-3 text-gray-400">•</span>
                        <span className="text-gray-600">
                          {isTeamCompetitor(competitor) ? 'Team' : 'Individual'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <svg
                    className="mx-auto h-16 w-16 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <h3 className="mt-4 text-lg font-semibold text-gray-900">No competitors yet</h3>
                  <p className="mt-2 text-gray-600">Add your first competitor to get started.</p>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Form Header */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Add New Competitor</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Select individual athlete or create a team
                </p>
              </div>

              {/* Form */}
              <CompetitorForm
                onSubmit={handleAddCompetitor}
                onCancel={handleCancelForm}
                loading={loading}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default CompetitorSidePanel;
